import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import COEHeader from './COEHeader';
import logoutManager from '../logout';
import api from '../api';
import AcademicContextSelector from './AcademicContextSelector';
import QuestionEditorModal from './QuestionEditorModal';

const AssignPaperToSubject = () => {
  const location = useLocation();
  const [user] = useState(location.state?.user || null);
  const [tenant, setTenant] = useState(location.state?.tenant || null);
  const [orgInfo, setOrgInfo] = useState(location.state?.orgInfo || { name: '', logoUrl: '' });
  const [academicContext, setAcademicContext] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [subjectSearch, setSubjectSearch] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [creatingAssignment, setCreatingAssignment] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('');
  const [endExamComponentId, setEndExamComponentId] = useState(null);
  
  // State for Question Editor Modal
  const [showQuestionEditor, setShowQuestionEditor] = useState(false);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [modalContext, setModalContext] = useState(null);
  const [existingAssignments, setExistingAssignments] = useState({});

  // State for Delete Confirmation Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const [deletingAssignment, setDeletingAssignment] = useState(false);

  useEffect(() => {
    if (!tenant) {
      setTenant({
        name: localStorage.getItem('tenantName') || '',
        subdomain: localStorage.getItem('tenantSubdomain') || '',
        id: localStorage.getItem('tenantId') || ''
      });
    }
  }, [tenant]);

  useEffect(() => {
    const tenantId = localStorage.getItem('tenantId') || tenant?.id || tenant?._id;
    if (tenantId) {
      api.get(`/api/organization/basic-info/${tenantId}`)
        .then((res) => setOrgInfo({ name: res.data?.name || '', logoUrl: res.data?.logoUrl || '' }))
        .catch(() => setOrgInfo({ name: '', logoUrl: '' }));
    }
  }, [tenant]);

  const handleLogout = () => logoutManager.logout({ redirectTo: '/' });
  const tenantId = localStorage.getItem('tenantId') || tenant?.id || tenant?._id;

  const showStatus = useCallback((message, type = 'info') => {
    setStatusMessage(message);
    setStatusType(type);
    setTimeout(() => {
      setStatusMessage('');
      setStatusType('');
    }, 5000);
  }, []);

  const isContextComplete = (ctx) => {
    if (!ctx) return false;
    if (!(ctx.programTypeId && ctx.programId && ctx.academicYearId)) return false;
    if (ctx.evaluationMode === 'sem') return !!ctx.semId;
    if (ctx.evaluationMode === 'term') return !!ctx.termId;
    return false;
  };

  const getContextSignature = (ctx) => {
    if (!ctx) return '';
    return [
      ctx.programTypeId || '',
      ctx.programId || '',
      ctx.academicYearId || '',
      ctx.evaluationMode || '',
      ctx.semId || '',
      ctx.termId || ''
    ].join('|');
  };

  const fetchEndExamComponentId = useCallback(async (ctx) => {
    try {
      const response = await api.get('/api/cie/cie-component', {
        params: {
          academicYearId: ctx.academicYearId,
          programId: ctx.programId,
          ...(ctx.evaluationMode ? { evaluationMode: ctx.evaluationMode } : {}),
          ...(ctx.semId ? { semId: ctx.semId } : {}),
          ...(ctx.termId ? { termId: ctx.termId } : {})
        }
      });

      const defs = response.data?.data || [];
      const matched = defs.find((d) => d?.endExam?.componentId || d?.endExam?._id || d?.endExam?.id);
      const compId = matched?.endExam?.componentId || matched?.endExam?._id || matched?.endExam?.id || null;
      return compId ? String(compId) : null;
    } catch (err) {
      console.error('Error fetching end exam component:', err);
      return null;
    }
  }, []);

  const checkExistingAssignment = useCallback(async (subjectId, ctx) => {
    try {
      const response = await api.get('/api/coe/end-exam-assignment/assignment/check', {
        params: {
          tenantId,
          programTypeId: ctx.programTypeId,
          programId: ctx.programId,
          academicYearId: ctx.academicYearId,
          semId: ctx.semId || undefined,
          termId: ctx.termId || undefined,
          subjectId
        },
        headers: { 'x-tenant-id': tenantId }
      });
      return response.data?.assignment || null;
    } catch (err) {
      console.error('Error checking existing assignment:', err);
      return null;
    }
  }, [tenantId]);

  const fetchSubjectsForContext = useCallback(async (ctx) => {
    try {
      console.log('[fetchSubjectsForContext] Fetching subjects with context:', {
        tenantId,
        programTypeId: ctx.programTypeId,
        programId: ctx.programId,
        academicYearId: ctx.academicYearId,
        evaluationMode: ctx.evaluationMode,
        semId: ctx.semId,
        termId: ctx.termId
      });

      const params = {
        tenantId: tenantId,
        programTypeId: ctx.programTypeId,
        programId: ctx.programId,
        academicYearId: ctx.academicYearId,
        evaluationMode: ctx.evaluationMode,
      };
      
      if (ctx.evaluationMode === 'sem' && ctx.semId) {
        params.semId = ctx.semId;
      } else if (ctx.evaluationMode === 'term' && ctx.termId) {
        params.termId = ctx.termId;
      }

      const response = await api.get('/api/coe/end-exam-assignment/subjects', {
        params,
        headers: { 'x-tenant-id': tenantId }
      });

      const subjectsData = response.data?.subjects || [];
      
      console.log(`[fetchSubjectsForContext] Found ${subjectsData.length} subjects from COE endpoint`);
      
      // Get program name from context
      const programName = ctx.programName || academicContext?.programName || 'Program';
      
      // Add program name to each subject
      const subjectsWithProgram = subjectsData.map(subject => ({
        ...subject,
        programName: programName
      }));
      
      // Check existing assignments for each subject
      const existingMap = {};
      for (const subject of subjectsWithProgram) {
        const assignment = await checkExistingAssignment(subject.subjectId, ctx);
        if (assignment && assignment.isFinalized) {
          existingMap[subject.subjectId] = assignment;
        }
      }
      setExistingAssignments(existingMap);
      
      return subjectsWithProgram;
    } catch (err) {
      console.error('Error fetching subjects from COE endpoint:', err);
      console.error('Error details:', err.response?.data);
      showStatus(err?.response?.data?.message || 'Failed to fetch subjects.', 'error');
      return [];
    }
  }, [tenantId, academicContext, checkExistingAssignment, showStatus]);

  const loadContextData = useCallback(async (ctx) => {
    if (!tenantId || !isContextComplete(ctx)) return;

    setLoadingSubjects(true);
    setSubjects([]);
    setSubjectSearch('');
    setTemplates([]);
    setSelectedSubject(null);
    setEndExamComponentId(null);
    showStatus('Loading subjects for selected context...', 'info');

    try {
      const [subjectRows, componentId] = await Promise.all([
        fetchSubjectsForContext(ctx),
        fetchEndExamComponentId(ctx)
      ]);

      setSubjects(subjectRows);
      setEndExamComponentId(componentId);

      if (!componentId) {
        showStatus('Subjects loaded, but no End Exam component found for this context.', 'warning');
      } else {
        showStatus(`Loaded ${subjectRows.length} subject records. Click a subject to fetch templates.`, 'success');
      }
    } catch (err) {
      console.error('Error loading context data:', err);
      showStatus(err?.response?.data?.message || 'Failed to load context data.', 'error');
    } finally {
      setLoadingSubjects(false);
    }
  }, [fetchSubjectsForContext, fetchEndExamComponentId, showStatus,tenantId]);

  const handleContextChange = useCallback((ctx) => {
    const nextSignature = getContextSignature(ctx);
    const prevSignature = getContextSignature(academicContext);
    if (nextSignature && nextSignature === prevSignature) return;
    
    setAcademicContext(ctx);
    if (isContextComplete(ctx)) {
      loadContextData(ctx);
    } else {
      setSubjects([]);
      setSubjectSearch('');
      setTemplates([]);
      setSelectedSubject(null);
      setEndExamComponentId(null);
      showStatus('Select full academic context to continue.', 'info');
    }
  }, [academicContext, loadContextData,showStatus]);

  const filteredSubjects = useMemo(() => {
    const query = subjectSearch.trim().toLowerCase();
    if (!query) return subjects;

    return subjects.filter((subject) => {
      const name = String(subject.subjectName || '').toLowerCase();
      const code = String(subject.subjectCode || '').toLowerCase();
      return name.includes(query) || code.includes(query);
    });
  }, [subjects, subjectSearch]);

  const handleSubjectClick = async (subjectRow) => {
    if (!academicContext || !tenantId) return;
    
    setSelectedSubject(subjectRow);
    setLoadingTemplates(true);
    setTemplates([]);
    showStatus(`Fetching templates for ${subjectRow.subjectName}...`, 'info');

    try {
      const response = await api.get('/api/coe/end-exam-assignment/templates', {
        params: {
          tenantId,
          programTypeId: academicContext.programTypeId,
          programId: academicContext.programId,
          status: 'active'
        },
        headers: { 'x-tenant-id': tenantId }
      });

      const list = (response.data?.templates || []).filter((t) => {
        const type = t.templateType || 'both';
        return type === 'both' || type === 'end_exam_only';
      });

      setTemplates(list);
      
      if (list.length === 0) {
        showStatus(`No eligible templates found for ${subjectRow.subjectName}.`, 'warning');
      } else {
        showStatus(`Loaded ${list.length} templates. Select one to configure question paper.`, 'success');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      showStatus(err?.response?.data?.message || 'Failed to fetch templates.', 'error');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateSelect = async (template) => {
    if (!academicContext || !selectedSubject || !tenantId) return;
    if (!endExamComponentId) {
      showStatus('End Exam component is missing for this context. Cannot create assignment.', 'error');
      return;
    }

    setCreatingAssignment(true);
    showStatus('Creating assignment and opening question editor...', 'info');

    try {
      const payload = {
        tenantId,
        templateId: template._id,
        componentId: endExamComponentId,
        itemId: null,
        programTypeId: academicContext.programTypeId,
        programId: academicContext.programId,
        academicYearId: academicContext.academicYearId,
        semId: academicContext.semId || null,
        termId: academicContext.termId || null,
        subjectId: selectedSubject.subjectId,
        subjectName: selectedSubject.subjectName,
        subjectCode: selectedSubject.subjectCode,
        actorId: user?.authUserId || user?.userId || user?._id,
        status: 'draft'
      };

      const response = await api.post('/api/coe/end-exam-assignment/assign', payload, {
        headers: { 'x-tenant-id': tenantId }
      });

      const assignment = response.data?.assignment;
      
      if (!assignment) {
        throw new Error('No assignment returned from server');
      }

      setCurrentAssignment(assignment);
      setCurrentTemplate(template);
      
      const contextForModal = {
        tenantId: tenantId,
        programTypeId: academicContext.programTypeId,
        programId: academicContext.programId,
        academicYearId: academicContext.academicYearId,
        semId: academicContext.semId || null,
        termId: academicContext.termId || null,
        subjectId: selectedSubject.subjectId,
        subjectName: selectedSubject.subjectName,
        subjectCode: selectedSubject.subjectCode,
        facultyId: user?.authUserId || user?.userId || user?._id,
        cieTemplateId: null,
        isEndExam: true
      };
      
      setModalContext(contextForModal);
      setShowQuestionEditor(true);
      
      showStatus(`Assignment created. Configure questions for ${selectedSubject.subjectName}.`, 'success');
    } catch (err) {
      console.error('Error creating assignment:', err);
      showStatus(err?.response?.data?.message || 'Failed to create assignment.', 'error');
    } finally {
      setCreatingAssignment(false);
    }
  };

  const handleModalClose = () => {
    setShowQuestionEditor(false);
    setCurrentAssignment(null);
    setCurrentTemplate(null);
    setModalContext(null);
  };

  const handleSaveAndFinalize = async (finalizedAssignment) => {
    if (finalizedAssignment && selectedSubject) {
      const key = selectedSubject.subjectId;
      setExistingAssignments(prev => ({ ...prev, [key]: finalizedAssignment }));
    }
    showStatus(`Question paper for ${selectedSubject?.subjectName} has been finalized and locked.`, 'success');
    handleModalClose();
    if (academicContext && isContextComplete(academicContext)) {
      await loadContextData(academicContext);
    }
  };

  const handleUpdateAssignment = (componentId, itemId, updatedAssignment) => {
    setCurrentAssignment(updatedAssignment);
  };

  const handleEditExistingAssignment = async (subjectRow) => {
    if (!academicContext || !tenantId) return;
    
    const key = subjectRow.subjectId;
    const existingAssignment = existingAssignments[key];
    if (!existingAssignment) {
      showStatus('No existing assignment found for this subject.', 'error');
      return;
    }
    
    setSelectedSubject(subjectRow);
    setCreatingAssignment(true);
    showStatus('Loading existing assignment configuration...', 'info');
    
    try {
      const response = await api.get(`/api/coe/end-exam-assignment/assignment/${existingAssignment._id}`, {
        headers: { 'x-tenant-id': tenantId }
      });
      
      const assignment = response.data?.assignment;
      if (!assignment) throw new Error('Failed to load assignment');
      
      const templateResponse = await api.get(`/api/coe/end-exam-assignment/templates`, {
        params: {
          tenantId,
          programTypeId: academicContext.programTypeId,
          programId: academicContext.programId,
          status: 'active'
        },
        headers: { 'x-tenant-id': tenantId }
      });
      
      const template = templateResponse.data?.templates?.find(t => t._id === assignment.templateId?.toString());
      if (!template) throw new Error('Template not found');
      
      setCurrentAssignment(assignment);
      setCurrentTemplate(template);
      
      const contextForModal = {
        tenantId: tenantId,
        programTypeId: academicContext.programTypeId,
        programId: academicContext.programId,
        academicYearId: academicContext.academicYearId,
        semId: academicContext.semId || null,
        termId: academicContext.termId || null,
        subjectId: subjectRow.subjectId,
        subjectName: subjectRow.subjectName,
        subjectCode: subjectRow.subjectCode,
        facultyId: user?.authUserId || user?.userId || user?._id,
        cieTemplateId: null,
        isEndExam: true
      };
      
      setModalContext(contextForModal);
      setShowQuestionEditor(true);
      showStatus(`Editing existing paper configuration for ${subjectRow.subjectName}.`, 'success');
    } catch (err) {
      console.error('Error loading existing assignment:', err);
      showStatus(err?.response?.data?.message || 'Failed to load existing assignment.', 'error');
    } finally {
      setCreatingAssignment(false);
    }
  };

  /**
   * Handle delete assignment click
   */
  const handleDeleteClick = (subjectRow) => {
    const key = subjectRow.subjectId;
    const assignment = existingAssignments[key];
    if (!assignment) {
      showStatus('No assignment found to delete.', 'error');
      return;
    }
    
    setAssignmentToDelete({
      assignment: assignment,
      subject: subjectRow
    });
    setShowDeleteModal(true);
  };

  /**
   * Confirm delete assignment
   */
  const confirmDeleteAssignment = async () => {
    if (!assignmentToDelete) return;
    
    setDeletingAssignment(true);
    showStatus(`Deleting assignment for ${assignmentToDelete.subject.subjectName}...`, 'info');
    
    try {
      const response = await api.delete(
        `/api/coe/end-exam-assignment/assignment/${assignmentToDelete.assignment._id}`,
        {
          headers: { 'x-tenant-id': tenantId }
        }
      );
      
      if (response.data.success) {
        showStatus(
          `Assignment for ${assignmentToDelete.subject.subjectName} deleted successfully. ${response.data.data?.studentMarksDeleted || 0} student mark records removed.`,
          'success'
        );
        
        // Refresh the subjects list
        if (academicContext && isContextComplete(academicContext)) {
          await loadContextData(academicContext);
        }
        
        // Clear selected subject if it was the deleted one
        if (selectedSubject && selectedSubject.subjectId === assignmentToDelete.subject.subjectId) {
          setSelectedSubject(null);
          setTemplates([]);
        }
      }
    } catch (err) {
      console.error('Error deleting assignment:', err);
      showStatus(err?.response?.data?.message || 'Failed to delete assignment.', 'error');
    } finally {
      setDeletingAssignment(false);
      setShowDeleteModal(false);
      setAssignmentToDelete(null);
    }
  };

  /**
   * Cancel delete
   */
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setAssignmentToDelete(null);
  };

  const getSubjectStatusBadge = (subjectRow) => {
    const key = subjectRow.subjectId;
    const assignment = existingAssignments[key];
    if (assignment && assignment.isFinalized) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Configured
        </span>
      );
    }
    return null;
  };

  const getTemplateTypeBadge = (type) => {
    if (type === 'end_exam_only') {
      return <span className="ml-2 px-2 py-0.5 text-[10px] font-bold bg-purple-100 text-purple-700 rounded-full">External Only</span>;
    }
    if (type === 'both') {
      return <span className="ml-2 px-2 py-0.5 text-[10px] font-bold bg-blue-100 text-blue-700 rounded-full">CIE + ESE</span>;
    }
    return null;
  };

  return (
    <div className="min-h-screen w-full flex flex-col bg-gradient-to-br from-slate-50 to-slate-100">
      <COEHeader user={user} tenant={tenant} orgInfo={orgInfo} onLogout={handleLogout} />

      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* Header Section */}
        <div className="mb-6">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-xl">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-slate-900">Assign Paper to Subject</h1>
                <p className="text-sm text-slate-500 mt-0.5">Configure End Examination question paper templates for subjects</p>
              </div>
            </div>
          </div>
        </div>

        {/* Context Selector */}
        <div className="mb-6">
          <AcademicContextSelector
            tenantId={tenantId}
            onContextChange={handleContextChange}
            initialContext={academicContext}
          />
        </div>

        {/* Status Message */}
        {statusMessage && (
          <div className={`mb-6 rounded-xl border p-4 ${
            statusType === 'error' ? 'bg-red-50 border-red-200 text-red-800' :
            statusType === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-800' :
            statusType === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
            'bg-blue-50 border-blue-200 text-blue-800'
          }`}>
            <div className="flex items-center gap-2">
              {statusType === 'success' && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              {statusType === 'error' && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              {statusType === 'warning' && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              )}
              <p className="text-sm font-medium">{statusMessage}</p>
            </div>
          </div>
        )}

        {/* Academic Context Display */}
        {academicContext && (
          <div className="mb-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100 p-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-600 mb-3">Selected Academic Context</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <p className="text-xs text-slate-500">Program Type</p>
                <p className="text-sm font-semibold text-slate-800 truncate">{academicContext.programTypeName || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Program</p>
                <p className="text-sm font-semibold text-slate-800 truncate">{academicContext.programName || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Academic Year</p>
                <p className="text-sm font-semibold text-slate-800 truncate">{academicContext.academicYearName || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Mode</p>
                <p className="text-sm font-semibold text-slate-800">{academicContext.evaluationMode === 'sem' ? 'Semester' : 'Term'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">{academicContext.evaluationMode === 'sem' ? 'Semester' : 'Term'}</p>
                <p className="text-sm font-semibold text-slate-800">{academicContext.semName || academicContext.termName || '-'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Main Content - Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Subjects Panel */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-indigo-100 rounded-lg">
                    <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <h3 className="text-sm font-bold text-slate-700">Subjects</h3>
                </div>
                <div className="flex items-center gap-2">
                  {loadingSubjects && (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-xs text-slate-500">Loading...</span>
                    </div>
                  )}
                  {subjects.length > 0 && (
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                      {filteredSubjects.length}/{subjects.length}
                    </span>
                  )}
                </div>
              </div>
              {subjects.length > 0 && (
                <div className="mt-3">
                  <label className="sr-only" htmlFor="subject-search">Search subjects</label>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m1.35-5.15a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </span>
                    <input
                      id="subject-search"
                      type="text"
                      value={subjectSearch}
                      onChange={(e) => setSubjectSearch(e.target.value)}
                      placeholder="Search by subject name or code"
                      className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-10 text-sm outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                    />
                    {subjectSearch && (
                      <button
                        type="button"
                        onClick={() => setSubjectSearch('')}
                        className="absolute inset-y-0 right-0 px-3 text-xs font-medium text-slate-500 hover:text-slate-700"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-4 max-h-[500px] overflow-y-auto">
              {subjects.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-12 h-12 mx-auto text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-sm text-slate-500">Select academic context to view subjects</p>
                </div>
              ) : filteredSubjects.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-12 h-12 mx-auto text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35m1.35-5.15a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <p className="text-sm text-slate-500">No subjects match your search</p>
                  <button
                    type="button"
                    onClick={() => setSubjectSearch('')}
                    className="mt-2 text-sm font-medium text-indigo-600 hover:text-indigo-800"
                  >
                    Clear search
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredSubjects.map((s) => {
                                    const isActive = selectedSubject && selectedSubject.subjectId === s.subjectId;
                                    const hasExisting = existingAssignments[s.subjectId]?.isFinalized;

                    return (
                      <div
                        key={s.subjectId}
                        onClick={() => handleSubjectClick(s)}
                        className={`group rounded-xl border-2 transition-all cursor-pointer overflow-hidden ${
                          isActive
                            ? 'border-indigo-400 bg-gradient-to-r from-indigo-50 to-white shadow-md'
                            : 'border-slate-200 hover:border-indigo-300 hover:shadow-sm bg-white'
                        }`}
                      >
                        <div className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap mb-2">
                                <h4 className="font-semibold text-slate-800">{s.subjectName}</h4>
                                {getSubjectStatusBadge(s)}
                              </div>
                              {/* Highlighted Subject Code */}
                              <div className="mb-2">
                                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                                  </svg>
                                  {s.subjectCode || 'N/A'}
                                </span>
                              </div>
                              {/* Program Name instead of Class Name */}
                              <div className="flex items-center gap-2 text-xs text-slate-500">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <span>{s.programName || academicContext?.programName || 'Program'}</span>
                              </div>
                            </div>
                            <svg className={`w-5 h-5 transition-transform ${isActive ? 'text-indigo-600 rotate-90' : 'text-slate-400 group-hover:text-indigo-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                          {hasExisting && (
                            <div className="mt-3 flex items-center gap-2">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditExistingAssignment(s);
                                }}
                                className="text-xs font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(s);
                                }}
                                className="text-xs font-medium text-rose-600 hover:text-rose-800 flex items-center gap-1"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Templates Panel */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-purple-100 rounded-lg">
                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-sm font-bold text-slate-700">Question Paper Templates</h3>
                </div>
                {(loadingTemplates || creatingAssignment) && (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-xs text-slate-500">Loading...</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-4 max-h-[500px] overflow-y-auto">
              {!selectedSubject ? (
                <div className="text-center py-12">
                  <svg className="w-12 h-12 mx-auto text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                  </svg>
                  <p className="text-sm text-slate-500">Select a subject to view available templates</p>
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-12 h-12 mx-auto text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-slate-500">No templates available for this subject</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {templates.map((t) => {
                    const isAlreadyConfigured = existingAssignments[selectedSubject.subjectId]?.isFinalized;
                    
                    return (
                      <button
                        key={t._id}
                        onClick={() => handleTemplateSelect(t)}
                        disabled={creatingAssignment || isAlreadyConfigured}
                        className={`w-full text-left rounded-xl border-2 transition-all overflow-hidden ${
                          isAlreadyConfigured 
                            ? 'border-slate-200 bg-slate-50 cursor-not-allowed opacity-60' 
                            : 'border-slate-200 hover:border-indigo-400 hover:shadow-md bg-white cursor-pointer'
                        }`}
                      >
                        <div className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-semibold text-slate-800">{t.name}</h4>
                                {getTemplateTypeBadge(t.templateType)}
                              </div>
                              <div className="flex items-center gap-3 mt-2">
                                <span className="text-xs text-slate-500">Max Marks: <span className="font-semibold text-slate-700">{t.maxMarks || 100}</span></span>
                                <span className="text-xs text-slate-500">Sections: <span className="font-semibold text-slate-700">{t.sections?.length || 0}</span></span>
                              </div>
                              {t.enforceCOPOMapping && (
                                <div className="mt-2 flex items-center gap-1">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-indigo-100 text-indigo-700">
                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    CO/PO Mapping Required
                                  </span>
                                </div>
                              )}
                            </div>
                            {!isAlreadyConfigured && (
                              <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                              </svg>
                            )}
                          </div>
                          {isAlreadyConfigured && (
                            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Subject already has a configured paper
                            </p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Question Editor Modal */}
      {showQuestionEditor && currentAssignment && currentTemplate && modalContext && (
        <QuestionEditorModal
          open={showQuestionEditor}
          onClose={handleModalClose}
          assignment={currentAssignment}
          template={currentTemplate}
          context={modalContext}
          onSaveAndFinalize={handleSaveAndFinalize}
          onUpdateAssignment={handleUpdateAssignment}
          userRole="coe"
          isCOE={true}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && assignmentToDelete && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-rose-50 px-6 py-4 border-b border-rose-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-rose-100 rounded-full">
                  <svg className="w-5 h-5 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-rose-800">Delete Assignment</h3>
              </div>
            </div>
            
            <div className="p-6">
              <p className="text-slate-700 mb-2">
                Are you sure you want to delete the End Exam assignment for:
              </p>
              <div className="bg-slate-50 rounded-xl p-3 mb-4">
                <p className="font-semibold text-slate-800">{assignmentToDelete.subject.subjectName}</p>
                <p className="text-xs text-slate-500 font-mono">{assignmentToDelete.subject.subjectCode}</p>
                <p className="text-xs text-slate-500 mt-1">Class: {assignmentToDelete.subject.className}</p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-amber-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-xs text-amber-700">
                    This will permanently delete the assignment and all associated student mark records. 
                    This action cannot be undone.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={cancelDelete}
                  disabled={deletingAssignment}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteAssignment}
                  disabled={deletingAssignment}
                  className="flex-1 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deletingAssignment ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete Permanently
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignPaperToSubject;